import { Transaction, type TransactionResult } from "@mysten/sui/transactions";

import { pollUntil } from "@/utils/sleep";

import { APTOS_CCTP_DOMAIN_ID } from "../nonce/contracts";
import type { CctpNetwork } from "./contracts";
import { SUI_CCTP_V2, SUI_DENY_LIST, SUI_GRAPHQL_URL, type SuiSharedObject } from "./contracts-sui";
import { MIN_FINALITY_THRESHOLD } from "./fees";

const BYTES32_RE = /^0x[0-9a-fA-F]{64}$/;

/** Output `index` of a command, which indexing the result can't type as always present. */
export const output = (result: TransactionResult, index: number) => ({
  $kind: "NestedResult" as const,
  NestedResult: [result.Result, index] as [number, number],
});
const ZERO_ADDRESS = `0x${"0".repeat(64)}`;

/** An owned USDC coin, as named in a transaction. */
export interface SuiCoinRef {
  objectId: string;
  version: string;
  digest: string;
}

/**
 * Circle's v2 burn from Sui to an Aptos account: split the amount off the owner's USDC, burn it,
 * have the USDC handler burn the coin, then complete. Sui has no Fast Transfer as a source, so the
 * burn is always Standard. Every shared object is named with its known version, so building needs
 * no RPC; only the owner's coins do.
 */
export function buildSuiBurnTransaction({
  network,
  coins,
  amount,
  recipient,
  maxFee,
}: {
  network: CctpNetwork;
  coins: SuiCoinRef[];
  amount: bigint;
  /** The Aptos account's 32 bytes. */
  recipient: string;
  maxFee: bigint;
}): Transaction {
  if (amount <= BigInt(0)) throw new Error("CCTP burn amount must be positive");
  if (!BYTES32_RE.test(recipient)) throw new Error(`Not a 32-byte Aptos address: ${recipient}`);
  const [primary, ...rest] = coins;
  if (!primary) throw new Error("No USDC on Sui to burn");

  const contracts = SUI_CCTP_V2[network];
  const tx = new Transaction();
  const shared = ({ objectId, initialSharedVersion }: SuiSharedObject, mutable: boolean) =>
    tx.sharedObjectRef({ objectId, initialSharedVersion, mutable });

  const source = tx.objectRef(primary);
  if (rest.length > 0)
    tx.mergeCoins(
      source,
      rest.map((coin) => tx.objectRef(coin)),
    );
  const split = tx.splitCoins(source, [tx.pure.u64(amount)]);

  const tokenMessengerState = shared(contracts.tokenMessengerMinterState, false);
  const burn = tx.moveCall({
    target: `${contracts.tokenMessengerMinterPackage}::deposit_for_burn::deposit_for_burn`,
    typeArguments: [contracts.usdcType],
    arguments: [
      split[0],
      tx.pure.u32(APTOS_CCTP_DOMAIN_ID),
      tx.pure.address(recipient),
      // Anyone may claim on Aptos.
      tx.pure.address(ZERO_ADDRESS),
      tx.pure.u256(maxFee),
      tx.pure.u32(MIN_FINALITY_THRESHOLD.standard),
      tx.pure.vector("u8", []),
      tokenMessengerState,
    ],
  });
  const handlerBurn = tx.moveCall({
    target: `${contracts.stablecoinHandlerPackage}::handler::burn`,
    arguments: [
      shared(contracts.stablecoinHandlerState, false),
      output(burn, 0),
      output(burn, 1),
      shared(SUI_DENY_LIST[network], false),
      shared(contracts.usdcTreasury, true),
    ],
  });
  tx.moveCall({
    target: `${contracts.tokenMessengerMinterPackage}::deposit_for_burn::complete_burn`,
    typeArguments: [contracts.usdcType, `${contracts.stablecoinHandlerPackage}::handler::Auth`],
    arguments: [
      output(handlerBurn, 0),
      tokenMessengerState,
      shared(contracts.messageTransmitterState, false),
    ],
  });
  return tx;
}

/** What a Sui burn reads from the network. */
export interface SuiBurnClient {
  getUsdcCoins(owner: string, network: CctpNetwork): Promise<SuiCoinRef[]>;
  waitForTransaction(
    digest: string,
  ): Promise<{ status: "success" } | { status: "failure"; error: string }>;
}

export interface SuiBurnWallet {
  address: string;
  signAndExecuteTransaction(transaction: Transaction): Promise<{ digest: string }>;
}

/** Burns USDC on Sui for a v2 deposit to Aptos. Returns the transaction digest. */
export async function burnOnSui({
  client,
  wallet,
  network,
  amount,
  recipient,
  maxFee,
}: {
  client: SuiBurnClient;
  wallet: SuiBurnWallet;
  network: CctpNetwork;
  amount: bigint;
  recipient: string;
  maxFee: bigint;
}): Promise<string> {
  const coins = await client.getUsdcCoins(wallet.address, network);
  const transaction = buildSuiBurnTransaction({ network, coins, amount, recipient, maxFee });
  transaction.setSender(wallet.address);
  const { digest } = await wallet.signAndExecuteTransaction(transaction);

  let result: Awaited<ReturnType<SuiBurnClient["waitForTransaction"]>>;
  try {
    result = await client.waitForTransaction(digest);
  } catch {
    // It may still land; dropping it here would offer a second burn of the same USDC.
    return digest;
  }
  if (result.status === "failure") {
    throw new Error(`CCTP v2 burn ${digest} failed on Sui: ${result.error}`);
  }
  return digest;
}

const COINS_QUERY = `query($owner: SuiAddress!, $type: String!, $after: String) {
  address(address: $owner) {
    objects(first: 50, after: $after, filter: { type: $type }) {
      nodes { address version digest }
      pageInfo { hasNextPage endCursor }
    }
  }
}`;

const STATUS_QUERY = `query($digest: String!) {
  transaction(digest: $digest) { effects { status executionError { message } } }
}`;

const POLL_MS = 1_000;
const CONFIRM_ATTEMPTS = 30;

/** One Sui GraphQL request. Throws on an HTTP or GraphQL error, never returns partial data. */
export async function suiGraphql<T>(
  network: CctpNetwork,
  query: string,
  variables: Record<string, unknown>,
  { fetchImpl = fetch }: { fetchImpl?: typeof fetch } = {},
): Promise<T> {
  const response = await fetchImpl(SUI_GRAPHQL_URL[network], {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`Sui GraphQL returned ${response.status}`);
  const body = (await response.json()) as { data?: T; errors?: Array<{ message: string }> };
  if (body.errors?.length) throw new Error(`Sui GraphQL: ${body.errors[0]?.message}`);
  if (!body.data) throw new Error("Sui GraphQL returned no data");
  return body.data;
}

const BALANCE_QUERY = `query($owner: SuiAddress!, $type: String!) {
  address(address: $owner) { balance(coinType: $type) { coinBalance } }
}`;

/**
 * The owner's USDC on Sui held in coin objects, in subunits. Not `totalBalance`, which adds the
 * address's balance accumulator: the burn spends only coins, so that part can't be deposited.
 */
export async function getSuiUsdcBalance(
  owner: string,
  network: CctpNetwork,
  options: { fetchImpl?: typeof fetch } = {},
): Promise<bigint> {
  interface Data {
    address: { balance: { coinBalance: string } | null } | null;
  }
  const data = await suiGraphql<Data>(
    network,
    BALANCE_QUERY,
    { owner, type: SUI_CCTP_V2[network].usdcType },
    options,
  );
  return BigInt(data.address?.balance?.coinBalance ?? 0);
}

/** A `SuiBurnClient` over Sui GraphQL. */
export function suiBurnClient(
  network: CctpNetwork,
  options: { fetchImpl?: typeof fetch } = {},
): SuiBurnClient {
  const graphql = <T>(query: string, variables: Record<string, unknown>) =>
    suiGraphql<T>(network, query, variables, options);

  return {
    async getUsdcCoins(owner) {
      const type = `0x2::coin::Coin<${SUI_CCTP_V2[network].usdcType}>`;
      const coins: SuiCoinRef[] = [];
      let after: string | null = null;
      do {
        interface Page {
          address: {
            objects: {
              nodes: Array<{ address: string; version: number; digest: string }>;
              pageInfo: { hasNextPage: boolean; endCursor: string | null };
            };
          } | null;
        }
        const data: Page = await graphql<Page>(COINS_QUERY, { owner, type, after });
        const page = data.address?.objects;
        if (!page) break;
        for (const node of page.nodes) {
          coins.push({
            objectId: node.address,
            version: String(node.version),
            digest: node.digest,
          });
        }
        after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
      } while (after);
      return coins;
    },

    async waitForTransaction(digest) {
      interface Status {
        transaction: {
          effects: { status: "SUCCESS" | "FAILURE"; executionError: { message: string } | null };
        } | null;
      }
      // The indexer can lag the wallet's execute by a moment.
      const effects = await pollUntil(
        async () => (await graphql<Status>(STATUS_QUERY, { digest })).transaction?.effects,
        { attempts: CONFIRM_ATTEMPTS, intervalMs: POLL_MS },
      );
      if (!effects) throw new Error(`Timed out waiting for Sui transaction ${digest}`);
      return effects.status === "SUCCESS"
        ? { status: "success" }
        : { status: "failure", error: effects.executionError?.message ?? "unknown" };
    },
  };
}
